import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Grid } from '../../ui/components/Grid/Grid';
import {
  Chip,
  ColumnsAndSettingsProvider,
  Dialog,
  ItemRow,
  LayoutBox,
  ListSearchControls,
  ListSearchDataRenderer,
  Main,
  Page,
  ProvideSearchAPIContext,
  useSearchApiContext,
} from '../../components';
import { ListDisplayTypes } from '../../constants';
import { locationSearchOptions } from '../../constants/searchData/searchOptions';
import { useApi, useAuth } from '../../hooks';
import { Box, Button, Heading, Spacing, Text, TextField } from '../../ui';
import { useEventCallback } from '../../ui/hooks';
import { withStyles } from '../../ui/styling';
import { formatMoney } from '../../utils';
import { LocationDialog } from '../SubmitApplicationPage/LocationDialog';

const defaultColDefs = [
  {
    label: (
      <Text bold uppercase>
        Location Name
      </Text>
    ),
    value: ({ name }) => name,
    props: {
      flex: 1,
    },
  },
  {
    label: (
      <Text bold uppercase>
        Address
      </Text>
    ),
    value: ({ displayAddress }) => displayAddress || 'No address available',
    props: { flex: 1.5 },
  },
  {
    label: (
      <Text bold uppercase>
        Customer
      </Text>
    ),
    value: ({ details }) => details?.customer_name || 'N/A',
    props: { flex: 1 },
  },
  {
    label: (
      <Text bold uppercase>
        Origin
      </Text>
    ),
    value: ({ details }) => details?.originating_number || '',
    props: { flex: 0.75 },
  },
  {
    label: (
      <Text bold uppercase>
        Status
      </Text>
    ),
    value: ({ details }) => details?.location_status || '',
    props: { flex: 0.5 },
  },
  {
    label: (
      <Text bold uppercase>
        Vehicles
      </Text>
    ),
    value: ({ details }) => {
      const vehicles = details?.num_vehicles || 0;
      const avgValue = details?.average_car_value || 0;
      return `${vehicles} ${avgValue > 0 ? `• Avg Value: $${formatMoney(avgValue)}` : ''}`;
    },
    props: { flex: 0.75 },
  },
];

const LocationRowItem = React.forwardRef(function LocationRowItem(props, ref) {
  const { item, onPress, ...rest } = props;

  const handleOnPress = useEventCallback(() => {
    if (onPress) {
      onPress(item);
    }
  });

  return <ItemRow ref={ref} item={item} onPress={onPress ? handleOnPress : undefined} {...rest} />;
});

const LocationCardItem = withStyles(({ theme }) => ({
  root: {
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: theme.colors.opacity('$gray.300', 0.3),
    px: theme.spacing(3),
    py: theme.spacing(1.5),
    bg: theme.colors.white,
    props: {
      gap: 0,
    },
  },
  topContainer: {
    flexDirection: theme.breakpoints({ xs: 'column', sm: 'row' }),
    justifyContent: theme.breakpoints({ xs: 'flex-start', sm: 'space-between' }),
  },
  contentLeft: {
    flex: theme.breakpoints({ xs: 1, sm: 1.5 }),
    props: {
      gap: 0,
    },
  },
  contentRight: {
    flex: theme.breakpoints({ xs: 1, sm: 0.5 }),
    justifyContent: 'flex-start',
    alignItems: theme.breakpoints({ xs: 'flex-start', sm: 'flex-end' }),
    props: {
      gap: 4,
    },
    padTop: theme.spacing(0.5),
  },
  bottomContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flex: 1,
    alignItems: 'flex-start',
    alignSelf: 'flex-start',
    padTop: theme.spacing(0.75),
  },
}))(
  React.forwardRef(function LocationCardItem(props, ref) {
    const { item, styles, onPress, ...rest } = props;
    const { name, displayAddress, details } = item || {};
    const {
      num_vehicles: numVehicles = 0,
      average_car_value: averageCarValue = 0,
      customer_name: customer,
      originating_number: originatingNumber,
      originating_type: originatingType,
      originating_risk_id: originatingRiskId,
      location_status: locationStatus,
    } = details || {};

    const handleOnPress = () => {
      if (onPress) {
        onPress(item);
      }
    };

    return (
      <Box ref={ref} onPress={handleOnPress} {...rest}>
        <Box style={styles.topContainer} {...styles.props.topContainer}>
          <Box style={styles.contentLeft} {...styles.props.contentLeft}>
            <Box flexDirection="row" alignItems="center" gap={6}>
              <Text size="xSmall" color="$gray.400" style={{ fontWeight: 500 }} uppercase>
                Location
              </Text>
              {locationStatus === 'deleted' && (
                <Chip color="#EF4444" textColor="#FFFFFF">
                  {locationStatus}
                </Chip>
              )}
              {locationStatus && locationStatus !== 'deleted' && (
                <Text size="xSmall" bold uppercase color="$green.600">
                  {locationStatus}
                </Text>
              )}
            </Box>
            <Heading level={4} numberOfLines={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </Heading>
            <Text small numberOfLines={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayAddress || 'No address available'}
            </Text>
          </Box>
          <Box style={styles.contentRight} {...styles.props.contentRight}>
            <Text small bold>
              {customer || 'N/A'}
            </Text>
            <Text size="xSmall" color="$gray.500">
              {`Originates from: ${
                originatingNumber
                  ? `${
                      originatingType === 'mce' ? 'MCE' : originatingType === 'quote' ? 'Quote' : 'Policy'
                    } ${originatingNumber}, Loc ${originatingRiskId}`
                  : 'none'
              }`}
            </Text>
          </Box>
        </Box>
        <Box style={styles.bottomContainer} {...styles.props.bottomContainer}>
          {numVehicles > 0 && (
            <Text small>
              {`${numVehicles} Vehicles`}
              {averageCarValue > 0 ? ` • Avg Value: $${formatMoney(averageCarValue)}` : ''}
            </Text>
          )}
        </Box>
      </Box>
    );
  })
);

const LocationEditModal = ({ open, onClose, data, onSubmit, processing }) => {
  const [formData, setFormData] = useState({
    name: '',
    visibleAddress: '',
  });

  React.useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        visibleAddress: data.visibleAddress || data.address || '',
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const isUnchanged = data && formData.name === (data.name || '') && formData.visibleAddress === (data.visibleAddress || data.address || '');

  const handleSubmit = () => {
    const updatedData = {
      ...data,
      ...formData,
      visibleAddress: formData.visibleAddress || data.address || '',
    };
    onSubmit(updatedData);
  };

  const actionButtonProps = {
    color: '$primary',
    disabled: processing,
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit Location"
      maxWidth="100%"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
      }}
      actions={
        <Box flexDirection="row" gap={2}>
          <Button variant="text" onPress={onClose} {...actionButtonProps} label="Cancel" />
          <Button onPress={handleSubmit} {...actionButtonProps} disabled={processing || isUnchanged} loading={processing} label="Save Changes" />
        </Box>
      }
    >
      <Box flexDirection="column" gap={3}>
        <TextField label="Location Name" value={formData.name} onChangeValue={(value) => handleChange('name', value)} />
        <TextField label="Location Address" value={formData.visibleAddress} onChangeValue={(value) => handleChange('visibleAddress', value)} />
      </Box>
    </Dialog>
  );
};
const LocationUpdateModal = ({ open, onClose, data, openLocationEditModal, openLocationFullDialog, setLocationFullDialog }) => {
  const [expanded, setExpanded] = useState(false);
  const [rowData, setRowData] = useState([]);
  const auth = useAuth();
  const { getLocationChanges } = useApi();
  const isAdminUser = auth.user?.roles.includes('dunvegan:internal');
  const gridApiRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const handleOpenLocationFromGrid = (locationData) => {
    if (openLocationFullDialog) {
      setLocationFullDialog({ open: true, data: locationData, fromGrid: true });
    }
  };
  const shorten = (str = '') => {
    if (!str) return '';
    const parts = String(str).split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).join(' ') + (parts.length > 2 ? '...' : '');
  };

  useEffect(() => {
    if (!expanded || !data?.id) return;
    const fetchData = async () => {
      setLoading(true);
      setRowData([]);
      try {
        const res = await getLocationChanges(data.id);
        const changes = Array.isArray(res) ? res : res.data || [];

        const formatted = changes.map((item) => {
          const oldName = item.oldName || '';
          const newName = item.newName || '';
          const oldVisible = item.oldVisibleAddress || '';
          const newVisible = item.newVisibleAddress || '';
          const oldBase = item.oldLocation?.address || '';
          const newBase = item.newLocation?.address || '';

          const baseChanged = item.oldLocation && item.newLocation;
          const nameChanged = oldName && newName && oldName !== newName;
          const visibleChanged = oldVisible && newVisible && oldVisible !== newVisible;

          let type = '';
          let oldVal = '';
          let newVal = '';

          if (baseChanged) {
            type = 'Base Address Change';
            oldVal = `${shorten(oldBase || oldVisible)} Open location`;
            newVal = `${shorten(newBase || newVisible)} Open location`;
          } else if (nameChanged && visibleChanged) {
            type = 'Name + Visible Address Change';
            oldVal = `${oldName} ${shorten(oldVisible)}`.trim();
            newVal = `${newName} ${shorten(newVisible)}`.trim();
          } else if (nameChanged) {
            type = 'Name Change';
            oldVal = oldName || 'N/A';
            newVal = newName || 'N/A';
          } else if (visibleChanged) {
            type = 'Visible Address Change';
            oldVal = oldVisible || 'N/A';
            newVal = newVisible || 'N/A';
          } else {
            return null;
          }

          return {
            type,
            old: oldVal,
            new: newVal,
            by: item.username || 'Unknown',
            oldLocation: item.oldLocation,
            newLocation: item.newLocation,
            date: new Date(item.changedAt).toISOString().split('T')[0],
          };
        });

        setRowData(formatted);
      } catch (err) {
        console.error('Error fetching location history', err);
        setRowData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [expanded, data?.id, getLocationChanges]);
  useEffect(() => {
    setExpanded(false);
  }, [data?.id]);

  const columnDefs = [
    { headerName: 'Type', field: 'type', flex: 1, wrapText: true },
    {
      headerName: 'Old',
      field: 'old',
      flex: 1,
      wrapText: true,
      cellRenderer: (params) => {
        if (params.value?.includes('Open location')) {
          const address = params.value.replace('Open location', '').trim();
          return (
            <Box display="flex" gap={2}>
              <Text style={{ color: 'black' }}>{address}</Text>
              <Button
                variant="outline"
                onPress={() => handleOpenLocationFromGrid(params.data.oldLocation)}
                label="Open location"
                style={{ color: 'black', fontSize: '9px' }}
              />
            </Box>
          );
        }
        return <span style={{ color: 'black' }}>{params.value}</span>;
      },
    },
    {
      headerName: 'New',
      field: 'new',
      flex: 1,
      wrapText: true,
      cellRenderer: (params) => {
        if (params.value?.includes('Open location')) {
          const address = params.value.replace('Open location', '').trim();
          return (
            <Box display="flex" gap={2}>
              <Text style={{ color: 'black' }}>{address}</Text>
              <Button
                variant="outline"
                onPress={() => handleOpenLocationFromGrid(params.data.newLocation)}
                label="Open location"
                style={{ color: 'black', fontSize: '9px' }}
              />
            </Box>
          );
        }
        return <span style={{ color: 'black' }}>{params.value}</span>;
      },
    },
    { headerName: 'By', field: 'by', flex: 1, wrapText: true },
    { headerName: 'Date', field: 'date', flex: 1, wrapText: true },
  ];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      styles={{
        dialogBox: {
          width: ({ theme }) =>
            isAdminUser ? theme.breakpoints({ xs: '90vw', sm: 640, md: '$mainWidth' }) : theme.breakpoints({ xs: '90vw', sm: 400, md: 500 }),
          maxWidth: '100%',
          maxHeight: '80vh',
          padX: ({ theme }) => theme.breakpoints({ xs: '$2', sm: '$4' }),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          background: 'white',
        },
      }}
    >
      <Box width="100%" display="flex" justifyContent="center" mb="$3">
        <Heading level={4} style={{ textAlign: 'center' }}>
          Update Location
        </Heading>
      </Box>

      <LayoutBox column padding="8px" gap={32} alignSelf="stretch" width="100%" justifyContent="center" alignItems="center">
        <Button
          color="$primary"
          onPress={() => {
            setExpanded(false);
            openLocationEditModal();
          }}
          label="Update Name or Visible Address"
          width="300px"
        />
        {isAdminUser && (
          <Button
            color="$primary"
            onPress={() => {
              setExpanded(false);
              openLocationFullDialog();
            }}
            label="Update Underlying Address"
            width="300px"
          />
        )}
        <Box
          width="100%"
          alignItems="center"
          justifyContent="flex-start"
          onPress={() => setExpanded(!expanded)}
          sx={{
            cursor: 'pointer',
            background: '#f5f5f5',
            borderRadius: '8px',
            padding: '12px 16px',
            width: '300px',
            transition: 'background 0.3s ease',
            '&:hover': { background: '#e0e0e0' },
          }}
        >
          {isAdminUser && (
            <Button color="$primary" onPress={() => setExpanded(!expanded)} label={`${expanded ? 'v' : '>'} Location History`} width="300px" />
          )}
        </Box>

        {expanded && (
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              height: '400px',
              mt: 2,
              borderRadius: '8px',
              overflowY: 'auto',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <div
                  className="spinner"
                  style={{
                    width: '20px',
                    height: '20px',
                    border: '4px solid #e0e0e0',
                    borderTop: '4px solid #5d9ee3ff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              </Box>
            )}

            <Grid
              data={rowData}
              columns={columnDefs}
              pageSize={10}
              shouldShowPagination={false}
              setGridReference={(params) => {
                gridApiRef.current = params.api;
              }}
              defaultColDef={{
                sortable: false,
                filter: false,
                resizable: false,
                wrapText: true,
                autoHeight: false,
                cellStyle: {
                  padding: '2px 4px',
                  lineHeight: '1.2em',
                  whiteSpace: 'normal',
                },
              }}
              rowHeight={75}
              headerHeight={40}
            />
          </Box>
        )}
      </LayoutBox>
    </Dialog>
  );
};
export const LocationView = () => {
  const { getLocations, updateLocation } = useApi();
  const searchOptions = locationSearchOptions;
  const listRef = useRef();
  const [locationDialog, setLocationDialog] = useState({ open: false, data: undefined });
  const [updateStatus, setUpdateStatus] = useState('idle');
  const [refreshKey, setRefreshKey] = useState(0);
  const [locationFullDialog, setLocationFullDialog] = useState({ open: false, data: undefined, fromGrid: false });
  const [updateLocationDialog, setUpdateLocationDialog] = useState({ open: false, data: undefined });
  const { displayType } = useSearchApiContext();

  const handleLocationEdit = useCallback((location) => {
    if (location) {
      setUpdateLocationDialog({ open: true, data: location });
    }
  }, []);

  const handleLocationEditSubmit = useCallback(
    async (data, isFullUpdate = false) => {
      try {
        const confirmed = window.confirm(
          'Warning – This change will modify the base record of the location. All previous references to this name or address will be updated.\n\nDo you want to continue?'
        );
        if (!confirmed) return;
        setUpdateStatus('pending');
        await updateLocation(data, isFullUpdate);
        setRefreshKey((prev) => prev + 1);

        const resetModalState = { open: false, data: undefined };
        setLocationDialog(resetModalState);
        setLocationFullDialog(resetModalState);
        setUpdateLocationDialog(resetModalState);
      } catch (error) {
        console.error('Error updating location:', error);
        const errorCode = error?.data?.code || error?.response?.data?.code;
        if (errorCode === 'UNIQUENESS_VIOLATION') {
          alert('A location with this name already exists for this customer. Please choose a different name.');
        } else {
          alert('Failed to update location. Please try again.');
        }
      } finally {
        setUpdateStatus('idle');
      }
    },
    [updateLocation]
  );

  const getLocationsWithOrigin = useCallback((config) => getLocations(config, true), [getLocations]);

  const renderHeader = ({ height, key, style }) => {
    return <ListSearchControls key={key} style={style} height={height} heading="Locations" searchPlaceholder="Search for locations" />;
  };

  return (
    <Page padTop={({ theme }) => theme.breakpoints({ xs: 0, sm: 0 })}>
      <Box width="100%" position="sticky" top={0} bg="$white" zIndex="$header" height={({ theme }) => theme.sizes.appBarHeight + theme.spacing(1)} />
      <Main
        maxWidth="100%"
        px={({ theme }) => theme.breakpoints({ xs: '$2', sm: '$6' })}
        layout="top-left"
        padTop={({ theme }) => theme.sizes.appBarHeight + theme.spacing(1)}
        padBottom="$16"
      >
        <Heading level={2} small>
          Administration
        </Heading>
        <Heading level={5}>for Locations</Heading>
        <Spacing vertical={1} />
        <LayoutBox
          width="100%"
          row
          layout="center"
          justifyContent="space-between"
          position="sticky"
          bg="$white"
          zIndex="$header"
          pt="$2"
          pb="$1"
          top={({ theme }) => theme.sizes.appBarHeight}
          border={{
            bottom: {
              width: 1,
              color: '$gray.200',
            },
          }}
        />

        <ProvideSearchAPIContext
          searchOptions={searchOptions}
          getData={getLocationsWithOrigin}
          appSettingsKey="LocationListView"
          cacheKey={`locationList-${refreshKey}`}
          listRef={listRef}
          defaultDisplayType={ListDisplayTypes.CARDS}
        >
          <ColumnsAndSettingsProvider viewKey="LocationListView" config={defaultColDefs}>
            <ListSearchDataRenderer
              ref={listRef}
              emptyMessage="No locations to show"
              renderHeader={renderHeader}
              cardItemComponent={(props) => <LocationCardItem {...props} />}
              tableItemComponent={LocationRowItem}
              defaultColDefs={defaultColDefs}
              displayType={displayType}
              stickyHeaderEnabled
              scrollNode
              onSelect={handleLocationEdit}
            />
          </ColumnsAndSettingsProvider>

          <LocationUpdateModal
            setLocationFullDialog={setLocationFullDialog}
            open={updateLocationDialog.open}
            data={updateLocationDialog.data}
            openLocationEditModal={() => setLocationDialog({ open: true, data: updateLocationDialog.data })}
            openLocationFullDialog={(location) => setLocationFullDialog({ open: true, data: location || updateLocationDialog.data, fromGrid: false })}
            onClose={() => setUpdateLocationDialog({ open: false })}
          />

          <LocationDialog
            open={locationFullDialog.open}
            data={locationFullDialog.data}
            onSubmit={locationFullDialog.fromGrid ? null : (data) => handleLocationEditSubmit(data, true)}
            onClose={() => setLocationFullDialog({ open: false, data: undefined, fromGrid: false })}
            mode={locationFullDialog.fromGrid ? 'VIEW' : 'EDIT'}
            canEdit={!locationFullDialog.fromGrid}
            inLocationView={!locationFullDialog.fromGrid}
            processing={updateStatus === 'pending'}
          />

          <LocationEditModal
            open={locationDialog.open}
            data={locationDialog.data}
            onSubmit={handleLocationEditSubmit}
            onClose={() => {
              setLocationDialog({ open: false });
            }}
            processing={updateStatus === 'pending'}
          />
        </ProvideSearchAPIContext>
      </Main>
    </Page>
  );
};
